import React, { useContext } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { finalDocumentAdmissionSchema } from 'app/edozvola/cases/final-document-admission/model/final-document-admission.schema';
import { Col, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import {
  determineIfDayIsNonWorkingMutation,
  getFinalDocumentAdmissionQuery,
  saveFinalDocumentAdmissionQuery,
} from 'app/edozvola/cases/final-document-admission/service/final-document-admission.queries';
import { useParams } from 'react-router-dom';
import { CaseTabContext } from 'app/edozvola/cases/case-form-wrapper';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { useDispatch } from 'react-redux';
import {
  APPEAL_DATE,
  initalFinalDocumentAdmissionModel,
} from 'app/edozvola/cases/final-document-admission/model/final-document-admission.model';
import { Option } from 'react-bootstrap-typeahead/types/types';
import moment from 'moment';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { getCaseContext } from 'app/shared/util/context-util';

const FinalDocumentAdmission = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { sidebarItemPair } = getCaseContext();

  const { mutate: saveFinalDocumentAdmission, isPending } = saveFinalDocumentAdmissionQuery(uuid, () => {});
  const { data: finalDocumentAdmissionData, isLoading } = getFinalDocumentAdmissionQuery(uuid);

  const formik = useFormik({
    initialValues: finalDocumentAdmissionData || initalFinalDocumentAdmissionModel,
    validationSchema: finalDocumentAdmissionSchema,
    onSubmit: values => saveFinalDocumentAdmission(values),
    initialTouched: { lastReceiptDate: true },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const addDaysToDate = (date, days: number) => {
    return moment(date).startOf('day').utc(true).add(days, 'day').toDate()
  }

  const { mutate: checkNonWorkingDay, isPending: checkinNonWorkingDays } = determineIfDayIsNonWorkingMutation(value =>
    formik.setFieldValue('appealDateExpiration', value),
  );

  const lastReceiptDateChanged = (fieldName, value: any) => {
    formik.setFieldValue(fieldName, value).then(() => {
      if (formik.values.appealDate) {
        updateAppealDateExpiration(value, formik.values.appealDate[0]);
      }
    });
  };

  const appealDateChanged = (selectedOption: Option[]) => {
    formik.setFieldValue('appealDate', selectedOption);
    if (selectedOption.length === 0) {
      formik.setFieldValue('appealDateExpiration', '');
    } else {
      updateAppealDateExpiration(formik.values.lastReceiptDate, selectedOption[0]);
    }
  };

  const updateAppealDateExpiration = (lastReceiptDate, appealDate) => {
    checkNonWorkingDay(appealDate === APPEAL_DATE.FIFTHTEENDAYS ? addDaysToDate(lastReceiptDate, 15) : addDaysToDate(lastReceiptDate, 30));
  };

  return (
    <FormikProvider value={formik}>
      <FormikValueObserver
        onChange={dirty => {
          if (dirty.dirty) {
            dispatch(setFormChanged(true));
          } else {
            dispatch(initCaseFormChange());
          }
        }}
      />
      <div>
        <h5 className={'form-header'}>Primitak završnog akta</h5>
        <div className={'d-flex gap-2 flex-column'}>
          <Row>
            <Col md={5}>
              <Label labelText={'Datum zadnjeg primitka'} />
              <Field
                id={`lastReceiptDate`}
                name={`lastReceiptDate`}
                render={({ field }) => (
                  <FormikDatepicker
                    field={field}
                    setFieldValue={lastReceiptDateChanged}
                    disabled={sidebarItemPair?.readOnly}
                    error={!sidebarItemPair?.readOnly && formik.errors.lastReceiptDate ? `${formik.errors.lastReceiptDate}` : ''}
                    dateFormat={APP_DATE_FORMAT}
                  />
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col md={5}>
              <Label labelText={'Rok za ulaganje žalbe/tužbe'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi rok za ulaganje žalbe/tužbe'}
                onItemSelect={selectedOption => appealDateChanged(selectedOption)}
                selected={formik.values.appealDate}
                labelKey={'value'}
                clearButton
                options={[APPEAL_DATE.FIFTHTEENDAYS, APPEAL_DATE.THIRTYDAYS]}
                disabled={sidebarItemPair?.readOnly}
              />
            </Col>
            <Col md={5}>
              <Label labelText={'Datum isteka roka za žalbu'} />
              <Field
                id={`appealDateExpiration`}
                name={`appealDateExpiration`}
                render={({ field }) => (
                  <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} disabled dateFormat={APP_DATE_FORMAT} />
                )}
              />
            </Col>
          </Row>
        </div>
      </div>
      {!sidebarItemPair?.readOnly && (
        <div className={'case-save-button'}>
          <Button
            label={'Spremi'}
            color={'info'}
            size={'lg'}
            disabled={!formik.dirty || !formik.isValid}
            isLoading={isPending}
            onClick={() => formik.handleSubmit()}
          />
        </div>
      )}
    </FormikProvider>
  );
};

export default FinalDocumentAdmission;
